import { Button, MenuItem, TextField, Typography } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import MatchInput from '../../matches/Input';
import { IMatchVideo, IMatchVideoPayload, MATCH_VIDEO_TITLES, MatchVideoTitle } from '../typings';

interface IProps extends Partial<InjectedIntlProps> {
  // Existing video to edit; omit for the creation form.
  initial?: IMatchVideo;
  // Lock the match (e.g. when adding from the match page).
  fixedMatch?: string | null;
  saving?: boolean;
  onSubmit: (payload: IMatchVideoPayload) => void;
  // Extra buttons rendered next to the submit one (e.g. delete on the edit form).
  extraActions?: React.ReactNode;
}

interface IState {
  url: string;
  match: string | null;
  title: MatchVideoTitle;
}

const iri = (entity: any): string | null => (entity && entity['@id'] ? entity['@id'] : null);

// Any YouTube URL form is accepted (watch, youtu.be, embed, shorts): the API normalises it.
const YOUTUBE_URL = /(youtube\.com|youtube-nocookie\.com|youtu\.be)\//i;

// @ts-ignore
@injectIntl
export class MatchVideoForm extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const v = props.initial;
    this.state = {
      url: v ? v.url : '',
      match: props.fixedMatch || (v ? iri(v.match) : null),
      title: (v && (MATCH_VIDEO_TITLES as readonly string[]).includes(v.title) ? v.title : 'Highlights') as MatchVideoTitle,
    };
  }

  public render() {
    const m = this.props.intl!.messages;
    const { fixedMatch, saving, extraActions } = this.props;
    const { url, match, title } = this.state;
    const urlLooksValid = !url || YOUTUBE_URL.test(url);

    return (
      <form onSubmit={this.handleSubmit} style={{ padding: 16 }}>
        <TextField
          label={m.field_youtubeUrl}
          value={url}
          onChange={(e) => this.setState({ url: e.target.value })}
          fullWidth
          required
          error={!urlLooksValid}
          helperText={urlLooksValid ? m.help_matchVideoUrl : m.error_matchVideoUrl}
          placeholder="https://www.youtube.com/watch?v=…"
          style={{ marginBottom: 16 }}
        />

        {!fixedMatch && (
          <div style={{ marginBottom: 16 }}>
            <MatchInput
              input={{
                name: 'match',
                value: match,
                onChange: (value: string | null) => this.setState({ match: value }),
              }}
            />
          </div>
        )}

        <TextField
          select
          label={m.field_title}
          value={title}
          onChange={(e) => this.setState({ title: e.target.value as MatchVideoTitle })}
          helperText={m.help_matchVideoTitle}
          style={{ minWidth: 220, marginBottom: 16 }}
        >
          {MATCH_VIDEO_TITLES.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 16 }}>
          {m.help_matchVideoMetadata}
        </Typography>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="submit" variant="contained" color="primary" disabled={!this.isFormValid() || saving}>
            <SaveIcon style={{ marginRight: 8 }} />
            {m.button_save}
          </Button>
          {extraActions}
        </div>
      </form>
    );
  }

  private isFormValid = () => {
    const { url, match } = this.state;
    return Boolean(url && YOUTUBE_URL.test(url) && match);
  };

  private handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!this.isFormValid()) return;

    const { url, match, title } = this.state;
    this.props.onSubmit({ url: url.trim(), match: match as string, title });
  };
}
